import PropTypes from 'prop-types';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';
import { fDateTime } from '../../../utils/formatTime';
import { fNumber } from '../../../utils/formatNumber';

export default function NotificationTrayItem({ item, onDismiss, onOpen, selected }) {
  const theme = useTheme();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.order_id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(item.order_id);
        }
      }}
      sx={{
        p: 1.4,
        borderRadius: 3,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: selected ? alpha(theme.palette.success.main, 0.32) : alpha(theme.palette.common.white, 0.22),
        bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.42 : 0.08),
        boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, 0.12)}`,
        backdropFilter: 'blur(22px)',
        WebkitBackdropFilter: 'blur(22px)',
        transition: theme.transitions.create(['transform', 'border-color', 'background-color'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: alpha(theme.palette.success.main, 0.4),
          bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.54 : 0.14),
        },
        '&:focus-visible': {
          outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: 2,
        },
        '&:hover .notification-dismiss, &:focus-within .notification-dismiss': {
          opacity: 1,
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {item.first_name} {item.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {item.order_id}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              label={item.viewed ? 'Viewed' : 'New'}
              color={item.viewed ? 'default' : 'success'}
              sx={{ height: 22, fontWeight: 700 }}
            />
            <IconButton
              size="small"
              className="notification-dismiss"
              onClick={(event) => {
                event.stopPropagation();
                onDismiss(item.order_id);
              }}
              sx={{
                opacity: item.viewed ? 1 : 0,
                transition: theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.shorter,
                }),
              }}
            >
              <Iconify icon="solar:close-circle-linear" width={16} height={16} />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {item.category || 'Giftcard order'}
          </Typography>
          <Typography variant="body2" fontWeight={700} noWrap>
            {item.currency} {fNumber(item.amount)}
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary" noWrap>
          Arrived {fDateTime(item.received_at || item.created_at)}
        </Typography>
      </Stack>
    </Box>
  );
}

NotificationTrayItem.propTypes = {
  item: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    category: PropTypes.string,
    created_at: PropTypes.string,
    currency: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    order_id: PropTypes.string.isRequired,
    received_at: PropTypes.string,
    viewed: PropTypes.bool,
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  selected: PropTypes.bool,
};
